import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Btn, F } from "@/lib/bricks";
import { usePalette, fieldStyle, FieldLabel } from "@/components/calm/Calm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { localDay } from "@/lib/localDay";
import { useAuth } from "@/contexts/AuthContext";
import HomeServicesList from "@/components/property/HomeServicesList";
import { SERVICES, checkBooking, defaultVisitDate, bookingBody, money, tierForSqft } from "@/pages/developer/propertyOps";

// Book one visit for a unit, space or property. The server re-checks every
// rule and the price (portfolio discount, HST), then hands back a Stripe
// Checkout link. Paid bookings land in this property's timeline.
export default function BookVisitDialog({ open, onClose, projectId, services, units = [], unitWord = "unit", preset = {}, discountPct = 0 }) {
  const { p, dark } = usePalette();
  const { user } = useAuth();
  const today = localDay();
  const first = preset.service_key && services.includes(preset.service_key) ? preset.service_key : services[0];
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    const u = units.find((x) => x.lot_id === preset.lot_id) || null;
    const sk = first;
    const mk = (SERVICES[sk] && SERVICES[sk].move && SERVICES[sk].move[0] === "move_out" && SERVICES[sk].move.length === 1) ? "move_out" : (preset.move_kind || "");
    setF({ service_key: sk, lot_id: u ? u.lot_id : "", unit: u ? u.unit : "", sqft: u && u.sqft ? String(u.sqft) : "",
      move_kind: mk, move_date: "", preferred_date: defaultVisitDate(sk, mk, "", today), site_contact_phone: "", access_notes: "" });
    setErr("");
  }, [open]);

  const s = SERVICES[f.service_key] || {};
  const needsUnit = s.scope === "unit" || s.scope === "space";
  const set = (k, v) => setF((o) => {
    const n = { ...o, [k]: v };
    if (k === "lot_id") { const u = units.find((x) => x.lot_id === v); n.unit = u ? u.unit : ""; n.sqft = u && u.sqft ? String(u.sqft) : ""; }
    if ((k === "lot_id" || k === "sqft") && SERVICES[n.service_key] && SERVICES[n.service_key].maxSqft) {
      const t = tierForSqft(n.sqft); if (t) n.service_key = t;
    }
    if (k === "service_key") {
      const ns = SERVICES[v] || {};
      if (ns.move && ns.move.length === 1) n.move_kind = ns.move[0];
      if (!ns.move) n.move_kind = "";
    }
    if (["service_key", "move_kind", "move_date"].includes(k)) n.preferred_date = defaultVisitDate(n.service_key, n.move_kind, n.move_date, today);
    return n;
  });
  const problem = useMemo(() => (f.service_key ? checkBooking(f, today) : ""), [f, today]);
  const cents = s.cents ? Math.round(s.cents * (100 - (discountPct || 0)) / 100) : null;

  async function go() {
    if (problem) { setErr(problem); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/dev/projects/${projectId}/book`, bookingBody(f));
      if (r.data && r.data.url) { window.location.assign(r.data.url); return; }
      setErr("Checkout didn’t open. Try again in a minute.");
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn’t start checkout.");
    }
    setBusy(false);
  }

  const field = { ...fieldStyle(p), width: "100%" };
  const row = { display: "flex", flexDirection: "column", gap: 6 };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="book-visit" style={{ maxWidth: 520, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 28, maxHeight: "90vh", overflowY: "auto", fontFamily: F.display, color: p.ink }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: F.display, fontSize: 20, fontWeight: 500, letterSpacing: -0.3, color: p.ink }}>Book a visit</DialogTitle>
          <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3 }}>
            {s.what || "Pick what you need."}
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
          <div style={row}>
            <FieldLabel htmlFor="bv-service">Service</FieldLabel>
            <select id="bv-service" data-testid="bv-service" value={f.service_key || ""} onChange={(e) => set("service_key", e.target.value)} style={field}>
              {services.map((k) => <option key={k} value={k}>{`${SERVICES[k].name} · ${money(SERVICES[k].cents)}`}</option>)}
            </select>
          </div>
          {needsUnit && (
            <div style={row}>
              <FieldLabel htmlFor="bv-unit">{unitWord.charAt(0).toUpperCase() + unitWord.slice(1)}</FieldLabel>
              <select id="bv-unit" data-testid="bv-unit" value={f.lot_id || ""} onChange={(e) => set("lot_id", e.target.value)} style={field}>
                <option value="">{`Pick a ${unitWord}`}</option>
                {units.map((u) => <option key={u.lot_id} value={u.lot_id}>{`${u.unit}${u.status_label ? ` · ${u.status_label}` : ""}`}</option>)}
              </select>
            </div>
          )}
          {s.maxSqft && (
            <div style={row}>
              <FieldLabel htmlFor="bv-sqft">Size (sq ft)</FieldLabel>
              <input id="bv-sqft" inputMode="numeric" value={f.sqft || ""} onChange={(e) => set("sqft", e.target.value)} style={field} />
            </div>
          )}
          {s.move && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={row}>
                <FieldLabel htmlFor="bv-move">Report for</FieldLabel>
                <select id="bv-move" data-testid="bv-move" value={f.move_kind || ""} onChange={(e) => set("move_kind", e.target.value)} style={field} disabled={s.move.length === 1}>
                  {s.move.length > 1 && <option value="">Pick one</option>}
                  {s.move.map((m) => <option key={m} value={m}>{m === "move_in" ? "Move-in" : "Move-out"}</option>)}
                </select>
              </div>
              <div style={row}>
                <FieldLabel htmlFor="bv-move-date">{f.move_kind === "move_in" ? "Move-in date" : "Move-out date"}</FieldLabel>
                <input id="bv-move-date" data-testid="bv-move-date" type="date" value={f.move_date || ""} onChange={(e) => set("move_date", e.target.value)} style={field} />
              </div>
            </div>
          )}
          <div style={row}>
            <FieldLabel htmlFor="bv-date">Visit date</FieldLabel>
            <input id="bv-date" data-testid="bv-date" type="date" value={f.preferred_date || ""} onChange={(e) => set("preferred_date", e.target.value)} style={field} />
            <span style={{ fontSize: 12, color: p.ink3 }}>We confirm the time with your contact.</span>
          </div>
          <div style={row}>
            <FieldLabel htmlFor="bv-phone">Who lets us in</FieldLabel>
            <input id="bv-phone" data-testid="bv-phone" placeholder="Phone number" value={f.site_contact_phone || ""} onChange={(e) => set("site_contact_phone", e.target.value)} style={field} />
            <textarea aria-label="Access notes" placeholder="Lockbox, concierge, tenant notice" rows={2} value={f.access_notes || ""} onChange={(e) => set("access_notes", e.target.value)} style={{ ...field, resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", borderTop: `1px solid ${p.hair}`, paddingTop: 14 }}>
            <span style={{ fontSize: 13, color: p.ink3 }}>{discountPct ? `Portfolio pricing, ${discountPct}% off. Plus HST.` : "Plus HST."}</span>
            <span data-testid="bv-price" style={{ fontFamily: F.mono, fontSize: 17 }}>{money(cents)}</span>
          </div>
          {err && <div role="alert" data-testid="bv-error" style={{ fontSize: 13, color: "var(--c-attn)" }}>{err}</div>}
          <Btn variant="accent" dark={dark} full disabled={busy} onClick={go} data-testid="bv-pay">{busy ? "Opening checkout…" : "Continue to payment"}</Btn>
          <span style={{ fontSize: 12, color: p.ink3, textAlign: "center" }}>Secure checkout. The booking shows here once it’s paid.</span>
          <HomeServicesList kind={user?.account_type} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
